/* -------------------------------------------------------
   useChatJobs  —  async-job chat hook
   Manages conversation state, job creation, exponential-
   backoff polling, stop, retry, and cleanup.
   ------------------------------------------------------- */

import { useState, useRef, useCallback, useEffect } from 'react';
import { createChatJob, getChatJob } from '../api/chatV2';
import type {
  ChatMessage,
  JobStatus,
  MessageRole,
  ConversationDetail,
} from '../types/chatV2';

/* ---------- helpers ---------- */

function uid(): string {
  try {
    return crypto.randomUUID();
  } catch {
    // fallback for older browsers
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }
}

/** Exponential backoff schedule (ms): 300 → 500 → 800 → 1200 → 2000 cap */
const BACKOFF = [300, 500, 800, 1200, 2000];
const delay = (n: number) => BACKOFF[Math.min(n, BACKOFF.length - 1)];

/* ---------- hook ---------- */

export function useChatJobs(userId: string, debugMode: boolean = false) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversationId, setConvId] = useState('');
  const [isSubmitting, setSubmitting] = useState(false);

  // Refs keep values fresh inside callbacks without re-creating them
  const convIdRef = useRef(conversationId);
  const msgsRef = useRef(messages);
  const mountedRef = useRef(true);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const aborts = useRef<Map<string, AbortController>>(new Map());

  convIdRef.current = conversationId;
  msgsRef.current = messages;

  // Clean up every polling timer + abort controller on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      timers.current.forEach((t) => clearTimeout(t));
      timers.current.clear();
      aborts.current.forEach((a) => a.abort());
      aborts.current.clear();
    };
  }, []);

  /* ---- patch a single message by id ---- */
  const patch = useCallback((id: string, u: Partial<ChatMessage>) => {
    if (!mountedRef.current) return;
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, ...u } : m)));
  }, []);

  /* ---- polling loop (recursive with backoff) ---- */
  const poll = useCallback(
    (msgId: string, jobId: string, attempt = 0) => {
      const ac = new AbortController();
      aborts.current.set(jobId, ac);

      const timer = setTimeout(async () => {
        if (ac.signal.aborted) return;
        try {
          const job = await getChatJob(jobId);
          if (ac.signal.aborted || !mountedRef.current) return;

          const u: Partial<ChatMessage> = {
            jobStatus: job.status,
            progressPct: job.progressPct,
          };

          // Stream partial content
          if (job.partialAnswerMarkdown) {
            u.partialContent = job.partialAnswerMarkdown;
            u.content = job.partialAnswerMarkdown;
          }

          // Terminal: COMPLETE
          if (job.status === 'COMPLETE') {
            u.content = job.finalAnswerMarkdown || job.partialAnswerMarkdown || '';
            u.finalContent = job.finalAnswerMarkdown;
            u.dataSources = job.dataSources;
            u.assumptions = job.assumptions;
            u.followups = job.followups;
            u.confidence = job.confidence;
            u.debug = job.debug;
            u.actionableItems = job.actionableItems ?? undefined;
            patch(msgId, u);
            timers.current.delete(jobId);
            aborts.current.delete(jobId);
            return;
          }

          // Terminal: FAILED
          if (job.status === 'FAILED') {
            u.error =
              job.finalAnswerMarkdown || 'Something went wrong. Please try again.';
            u.content = u.error;
            patch(msgId, u);
            timers.current.delete(jobId);
            aborts.current.delete(jobId);
            return;
          }

          // In-progress — update & schedule next tick
          patch(msgId, u);
          if (!ac.signal.aborted) poll(msgId, jobId, attempt + 1);
        } catch {
          if (ac.signal.aborted || !mountedRef.current) return;
          // Network errors — retry with backoff up to ~20 attempts
          if (attempt < 20) {
            poll(msgId, jobId, attempt + 1);
          } else {
            patch(msgId, {
              jobStatus: 'FAILED' as JobStatus,
              error: 'Unable to reach the server. Please check your connection.',
              content: 'Unable to reach the server. Please check your connection.',
            });
            timers.current.delete(jobId);
            aborts.current.delete(jobId);
          }
        }
      }, delay(attempt));

      timers.current.set(jobId, timer);
    },
    [patch],
  );

  /* ---- send a new user message ---- */
  const sendMessage = useCallback(
    async (text: string) => {
      // Ensure we have a conversation id (generate on first message)
      let cid = convIdRef.current;
      if (!cid) {
        cid = uid();
        convIdRef.current = cid;
        setConvId(cid);
      }

      // Add user + pending assistant message
      const userMsg: ChatMessage = {
        id: uid(),
        role: 'user',
        content: text,
        timestamp: Date.now(),
      };
      const asstId = uid();
      const asstMsg: ChatMessage = {
        id: asstId,
        role: 'assistant',
        content: '',
        timestamp: Date.now(),
        jobStatus: 'QUEUED',
        progressPct: 0,
        originalMessage: text,
      };
      setMessages((prev) => [...prev, userMsg, asstMsg]);
      setSubmitting(true);

      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const { jobId } = await createChatJob({
          userId,
          conversationId: cid,
          message: text,
          timezone: tz,
          debug: debugMode,
        });
        if (!mountedRef.current) return;
        patch(asstId, { jobId });
        poll(asstId, jobId);
      } catch (err: any) {
        if (!mountedRef.current) return;
        patch(asstId, {
          jobStatus: 'FAILED',
          error: err?.message || 'Failed to send.',
          content: 'Failed to send your message. Please try again.',
        });
      } finally {
        if (mountedRef.current) setSubmitting(false);
      }
    },
    [userId, debugMode, patch, poll],
  );

  /* ---- stop polling for one message (client-side cancel) ---- */
  const stopJob = useCallback(
    (msgId: string) => {
      const msg = msgsRef.current.find((m) => m.id === msgId);
      if (msg?.jobId) {
        const t = timers.current.get(msg.jobId);
        if (t) {
          clearTimeout(t);
          timers.current.delete(msg.jobId);
        }
        const a = aborts.current.get(msg.jobId);
        if (a) {
          a.abort();
          aborts.current.delete(msg.jobId);
        }
      }
      patch(msgId, { stoppedByUser: true });
    },
    [patch],
  );

  /* ---- retry a failed or stopped message ---- */
  const retry = useCallback(
    async (msgId: string) => {
      const msg = msgsRef.current.find((m) => m.id === msgId);
      if (!msg?.originalMessage) return;

      // Reset the assistant message back to QUEUED
      patch(msgId, {
        content: '',
        jobStatus: 'QUEUED',
        progressPct: 0,
        error: undefined,
        stoppedByUser: false,
        jobId: undefined,
        dataSources: undefined,
        assumptions: undefined,
        followups: undefined,
        confidence: undefined,
        debug: undefined,
        finalContent: undefined,
        partialContent: undefined,
      });
      setSubmitting(true);

      try {
        let cid = convIdRef.current;
        if (!cid) {
          cid = uid();
          convIdRef.current = cid;
          setConvId(cid);
        }
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const { jobId } = await createChatJob({
          userId,
          conversationId: cid,
          message: msg.originalMessage,
          timezone: tz,
          debug: debugMode,
        });
        if (!mountedRef.current) return;
        patch(msgId, { jobId });
        poll(msgId, jobId);
      } catch (err: any) {
        if (!mountedRef.current) return;
        patch(msgId, {
          jobStatus: 'FAILED',
          error: err?.message || 'Failed to send.',
          content: 'Failed to send your message. Please try again.',
        });
      } finally {
        if (mountedRef.current) setSubmitting(false);
      }
    },
    [userId, debugMode, patch, poll],
  );

  /* ---- load a persisted conversation from history ---- */
  const loadConversation = useCallback(
    (detail: ConversationDetail) => {
      // Stop any current polling
      timers.current.forEach((t) => clearTimeout(t));
      timers.current.clear();
      aborts.current.forEach((a) => a.abort());
      aborts.current.clear();

      // Set conversation id
      const cid = detail.id;
      convIdRef.current = cid;
      setConvId(cid);

      // Hydrate backend messages → client ChatMessage[]
      const msgs: ChatMessage[] = detail.messages.map((m) => ({
        id: m.id || uid(),
        role: m.role.toLowerCase() as MessageRole,
        content: m.content,
        timestamp: new Date(m.createdAt).getTime(),
        // Mark assistant messages as already complete
        ...(m.role === 'ASSISTANT'
          ? { jobStatus: 'COMPLETE' as JobStatus }
          : {}),
      }));

      setMessages(msgs);
      setSubmitting(false);
    },
    [],
  );

  /* ---- reset entire conversation ---- */
  const reset = useCallback(() => {
    timers.current.forEach((t) => clearTimeout(t));
    timers.current.clear();
    aborts.current.forEach((a) => a.abort());
    aborts.current.clear();
    setMessages([]);
    setConvId('');
    convIdRef.current = '';
  }, []);

  /* ---- derived: count of in-flight jobs ---- */
  const activeJobs = messages.filter(
    (m) =>
      m.role === 'assistant' &&
      m.jobStatus &&
      !['COMPLETE', 'FAILED'].includes(m.jobStatus) &&
      !m.stoppedByUser,
  ).length;

  return {
    messages,
    conversationId,
    isSubmitting,
    activeJobs,
    sendMessage,
    stopJob,
    retry,
    reset,
    loadConversation,
  };
}
